/**
 * engine/formato.ts — formatadores pt-BR.
 *
 * NÃO é motor: são funções de APRESENTAÇÃO. Vivem aqui porque `calcViab` (v67 1575-1591)
 * mistura regra e texto — os 5 motivos já saem como frases formatadas, e a regra desta
 * fatia é não mudar comportamento. Por isso `calcViab` recebe os formatadores por
 * INJEÇÃO (`FormatadoresViab`), com estes defaults idênticos aos do protótipo.
 *
 * ⚠ Dependem do ICU do runtime: em Node/Cloudflare o resultado pode diferir do browser
 * (espaço fino vs. espaço normal depois de "R$", por exemplo). Isso afeta só as STRINGS
 * `motivos[].m` e `motivos[].kpi` — nunca os booleanos `ok`/`viavel` nem os números.
 */

import { ymLabel } from './datas';
import type { Ym } from './tipos';

/** v67 linha 1312. */
export const fmtBRL = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  minimumFractionDigits: 2,
});

/** v67 linha 1313. */
export const moeda = (v: number): string => fmtBRL.format(isFinite(v) ? v : 0);

/** v67 linha 1314 — moeda sem centavos. Usada dentro de `calcViab`. */
export const moeda0 = (v: number): string =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 })
    .format(isFinite(v) ? v : 0);

/** v67 linha 1321 — número pt-BR. Usada dentro de `calcViab`. */
export const num = (v: number): string => new Intl.NumberFormat('pt-BR').format(v);

/** v67 linha 1315. */
export const fmtNum2 = (v: number | string | null | undefined): string =>
  (v == null || v === '') ? '' : Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** v67 linha 1319 — fração → '12,34%'. */
export const pct = (v: number): string =>
  (isFinite(v) ? (v * 100).toFixed(2) : '0,00').replace('.', ',') + '%';

/** v67 linha 1320 — fração → '12,3%'. */
export const pct1 = (v: number): string =>
  (isFinite(v) ? (v * 100).toFixed(1) : '0,0').replace('.', ',') + '%';

/** v67 linha 1317 — percentual compacto da matriz de Obras ("7" → '7%', "7,5" → '7,5%'). */
export const fmtPc = (v: number | null | undefined): string =>
  (Math.round((v || 0) * 100) / 100).toString().replace('.', ',') + '%';

/** v67 linha 1316 — moeda com o "R$ " embutido no valor (célula da tabela de unidades). */
export const fmtMoeda = (v: number | string | null | undefined): string =>
  (v == null || v === '') ? '' : 'R$ ' + Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * Notação científica: `1e3`, `1,5E+4`, `-2.5e-3`.
 *
 * Reconhecida ANTES da limpeza, e só quando a string INTEIRA é um número nesse formato —
 * qualquer coisa a mais cai no caminho normal. Vírgula vale como separador decimal aqui
 * também, porque é o que o Excel em pt-BR escreve.
 */
const CIENTIFICA = /^[+-]?\d+(?:[.,]\d+)?[eE][+-]?\d+$/;

/**
 * v67 linha 1318 — parser pt-BR ("R$ 1.234,56" → 1234.56).
 *
 * ⚠ DIVERGÊNCIA DELIBERADA DA v67 (autorizada em 27/07/2026), e a única até aqui.
 * A limpeza `[^\d,.-]` apaga a letra "e" e COLA os dígitos que sobram: `1e3` virava
 * **13** e `-1e5` virava **-15**. Não erro, não zero — um número plausível e errado
 * entrando no cálculo como se tivesse sido digitado. É o formato que uma planilha
 * exportada de outro sistema usa para 1.000, e o produto lê planilha do usuário.
 *
 * Tudo o mais fica IGUAL, e isso importa: ponto é DECIMAL quando não há vírgula
 * (`0.897` → 0,897, de que a importação de percentual depende) e é separador de milhar
 * quando há (`1.234,56` → 1234,56). A função continua sem nunca devolver NaN.
 */
export function parseNum(s: string | number | null | undefined): number {
  if (s == null) return 0;
  const cru = String(s).trim();
  if (CIENTIFICA.test(cru)) return parseFloat(cru.replace(',', '.')) || 0;
  let t = cru.replace(/[^\d,.-]/g, '');
  if (t.indexOf(',') >= 0) t = t.replace(/\./g, '').replace(',', '.');
  return parseFloat(t) || 0;
}

/** Formatadores que `calcViab` consome. Injetáveis para tornar o motor testável fora do browser. */
export interface FormatadoresViab {
  moeda0: (v: number) => string;
  num: (v: number) => string;
  ymLabel: (ym: Ym) => string;
}

/** Default: exatamente o que o protótipo usa. */
export const FORMATADORES_PADRAO: FormatadoresViab = { moeda0, num, ymLabel };
